import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useLanguage } from '../i18n/LanguageContext';
import { OFFERS, pick, type Lang } from '../offers/config';
import { DOCS } from '../legal/docs';
const SITE = 'https://blinkblis.com';
const OG_IMAGE = 'https://cdn.magicpatterns.com/uploads/8L4B6PS8W7uWmJpvwft9Jj/blink-blis-high-resolution-logo-transparent.png';
const HOME: Record<Lang, {
  title: string;
  description: string;
}> = {
  es: {
    title: 'Blinkblis — Decisiones mas inteligentes sobre dinero, credito e IA',
    description: 'Blinkblis ayuda a tomar mejores decisiones sobre dinero, credito, IA y herramientas digitales mediante investigacion transparente, comparaciones y guias practicas.'
  },
  en: {
    title: 'Blinkblis — Smarter Decisions for Money, Credit & AI',
    description: 'Blinkblis helps people make smarter decisions about money, credit, AI and digital tools through transparent research, comparisons and practical guidance.'
  }
};
const LEGAL_DESC: Record<string, Record<Lang, string>> = {
  privacidad: {
    es: 'Como Blinkblis recopila, utiliza y protege la informacion de los usuarios, y que derechos tienes sobre tus datos.',
    en: 'How Blinkblis collects, uses and protects user information, and what rights you have over your data.'
  },
  terminos: {
    es: 'Terminos y condiciones de uso de Blinkblis, incluyendo los limites de nuestro contenido informativo.',
    en: 'Terms and conditions for using Blinkblis, including the limits of our informational content.'
  },
  metodologia: {
    es: 'Como evaluamos productos, que significa nuestro Nivel de Confianza y con que criterios construimos cada recomendacion.',
    en: 'How we evaluate products, what our Confidence Level means and the criteria behind every recommendation.'
  },
  'estandares-editoriales': {
    es: 'Las reglas con las que producimos contenido en Blinkblis: independencia, divulgacion y correccion de errores.',
    en: 'The rules we produce content by at Blinkblis: independence, disclosure and error correction.'
  },
  'divulgacion-de-afiliados': {
    es: 'Como gana dinero Blinkblis, que significa un enlace de afiliado y por que eso no cambia lo que recomendamos.',
    en: 'How Blinkblis makes money, what an affiliate link means and why it does not change what we recommend.'
  }
};
function upsert(selector: string, create: () => HTMLElement, apply: (el: HTMLElement) => void) {
  let el = document.head.querySelector(selector) as HTMLElement | null;
  if (!el) {
    el = create();
    document.head.appendChild(el);
  }
  apply(el);
}
function setMeta(attr: 'name' | 'property', key: string, value: string) {
  upsert(`meta[${attr}="${key}"]`, () => {
    const m = document.createElement('meta');
    m.setAttribute(attr, key);
    return m;
  }, el => el.setAttribute('content', value));
}
export function SeoManager() {
  const {
    pathname
  } = useLocation();
  const {
    language
  } = useLanguage();
  const lang = (language === 'en' ? 'en' : 'es') as Lang;
  useEffect(() => {
    const clean = pathname.replace(/\/+$/, '') || '/';
    let title = HOME[lang].title;
    let description = HOME[lang].description;
    let canonical = `${SITE}/`;
    let type = 'website';
    const offerSlug = clean.startsWith('/oferta/') ? clean.replace('/oferta/', '') : null;
    const offer = offerSlug ? OFFERS.find(o => o.slug === offerSlug) : undefined;
    if (offer) {
      title = `${pick(offer.title, lang)} | Blinkblis`;
      description = pick(offer.subtitle, lang);
      canonical = `${SITE}/oferta/${offer.slug}`;
      type = 'article';
    } else {
      const legalSlug = clean.slice(1);
      const doc = DOCS[legalSlug];
      if (doc) {
        title = `${doc.title} | Blinkblis`;
        description = LEGAL_DESC[legalSlug]?.[lang] || `${doc.title} — Blinkblis. ${doc.updated}.`;
        canonical = `${SITE}/${legalSlug}`;
        type = 'article';
      }
    }
    document.title = title;
    document.documentElement.lang = lang;
    setMeta('name', 'description', description);
    setMeta('name', 'robots', 'index, follow');
    setMeta('property', 'og:title', title);
    setMeta('property', 'og:description', description);
    setMeta('property', 'og:url', canonical);
    setMeta('property', 'og:type', type);
    setMeta('property', 'og:image', OG_IMAGE);
    setMeta('property', 'og:site_name', 'Blinkblis');
    setMeta('property', 'og:locale', lang === 'en' ? 'en_US' : 'es_US');
    setMeta('name', 'twitter:card', 'summary_large_image');
    setMeta('name', 'twitter:title', title);
    setMeta('name', 'twitter:description', description);
    setMeta('name', 'twitter:image', OG_IMAGE);
    upsert('link[rel="canonical"]', () => {
      const l = document.createElement('link');
      l.setAttribute('rel', 'canonical');
      return l;
    }, el => el.setAttribute('href', canonical));
  }, [pathname, lang]);
  return null;
}